import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { useOmniAuth } from '@omni/auth'
import { ArrowRight, Loader2, Search } from 'lucide-react'
import { useVisibleSurfaces } from '../surfaces'
import { makeMailApiGet } from '../lib/mail-api'
import { runSearch, type PaletteResult } from '../lib/search'
import { shellNavigate } from '../lib/navigation'
import { useShellStore } from '../lib/store'

interface Row {
  key: string
  group: string
  title: string
  subtitle?: string
  href: string
}

export function CommandPalette({ placeholder }: { placeholder?: string } = {}) {
  const open = useShellStore((s) => s.paletteOpen)
  const setOpen = useShellStore((s) => s.setPaletteOpen)
  const { getClient } = useOmniAuth()
  const surfaces = useVisibleSurfaces()

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PaletteResult[]>([])
  const [searching, setSearching] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  const mailApiGet = useMemo(() => makeMailApiGet(getClient('mail')), [getClient])

  const close = useCallback(() => {
    abortRef.current?.abort()
    setOpen(false)
    setQuery('')
    setResults([])
    setSearching(false)
    setActiveIndex(0)
  }, [setOpen])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // Debounced fan-out to the surface search providers (crm + projects always;
  // mail only when its API is configured).
  useEffect(() => {
    if (!open) return
    abortRef.current?.abort()
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setResults([])
      setSearching(false)
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    setSearching(true)
    const timer = setTimeout(() => {
      runSearch(trimmed, controller.signal, mailApiGet, getClient('crm'))
        .then((r) => {
          if (!controller.signal.aborted) {
            setResults(r)
            setActiveIndex(0)
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false)
        })
    }, 150)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [open, query, mailApiGet])

  const rows = useMemo<Row[]>(() => {
    const trimmed = query.trim().toLowerCase()
    const nav: Row[] = surfaces
      .filter(
        ({ meta }) => !trimmed || meta.label.toLowerCase().includes(trimmed),
      )
      .map(({ meta }) => ({
        key: `nav:${meta.id}`,
        group: 'Go to',
        title: meta.label,
        subtitle: meta.basePath,
        href: meta.basePath,
      }))
    const found: Row[] = results.map((r) => ({
      key: r.key,
      group: r.appLabel,
      title: r.title,
      subtitle: r.subtitle,
      href: r.href,
    }))
    return [...found, ...nav]
  }, [query, results, surfaces])

  const select = useCallback(
    (row: Row) => {
      // A hit in one of an instance's own apps carries that app's full address.
      if (/^https:\/\//.test(row.href)) window.location.assign(row.href)
      else shellNavigate(row.href)
      close()
    },
    [close],
  )

  function onKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, rows.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const row = rows[activeIndex]
      if (row) select(row)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      close()
    }
  }

  if (!open) return null

  let lastGroup = ''
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-surface-1 shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border px-4">
          {searching ? (
            <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
          ) : (
            <Search className="h-4 w-4 text-text-muted" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Search customers, contacts, projects, mail…'}
            className="w-full bg-transparent py-3.5 text-sm text-text-primary outline-none placeholder:text-text-muted"
          />
          <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px] text-text-muted">
            esc
          </kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto py-1">
          {rows.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-text-muted">
              {query.trim().length < 2
                ? 'Type to search across every surface.'
                : searching
                  ? 'Searching…'
                  : 'No results.'}
            </p>
          )}
          {rows.map((row, i) => {
            const showGroup = row.group !== lastGroup
            lastGroup = row.group
            return (
              <div key={row.key}>
                {showGroup && (
                  <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                    {row.group}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => select(row)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm ${
                    i === activeIndex
                      ? 'bg-accent-muted text-text-primary'
                      : 'text-text-secondary'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-text-primary">
                      {row.title}
                    </span>
                    {row.subtitle && (
                      <span className="block truncate text-xs text-text-muted">
                        {row.subtitle}
                      </span>
                    )}
                  </span>
                  {i === activeIndex && (
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                  )}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
